import { useTranslation } from "react-i18next"
import { Button, Stack } from "@mui/material"

import OTPInput from "./OTPInput"

const OTP_LENGTH = 6
const NON_DIGITS = /\D/g

interface OTPCodeFieldProps {
  value: string
  onChange: (value: string) => void
  onComplete: (value: string) => void | Promise<void>
}

/** The six-digit code field plus its "paste code" button, shared by both 2FA screens. */
const OTPCodeField = ({ value, onChange, onComplete }: OTPCodeFieldProps) => {
  const { t } = useTranslation()

  const handlePasteClick = async () => {
    try {
      // `readText` rejects when the user denies clipboard permission, and this runs
      // straight from a click handler, so there is no one to catch it further up.
      const clipboard = await navigator.clipboard.readText()
      const code = clipboard.replace(NON_DIGITS, "").slice(0, OTP_LENGTH)
      // Nothing to paste: leave what the user already typed alone, the way
      // `OTPInput.handlePaste` does for a Ctrl+V with no digits in it.
      if (!code) return
      onChange(code)
      if (code.length === OTP_LENGTH) await onComplete(code)
    } catch {
      // Clipboard permission denied: leave the input as the user left it.
    }
  }

  return (
    <Stack gap={1} justifyContent="center" alignItems="center">
      <OTPInput
        value={value}
        onChange={onChange}
        onComplete={onComplete}
        autoFocus
        length={OTP_LENGTH}
        gap="5px"
        sx={{
          maxWidth: "350px",
          "& .MuiOutlinedInput-input": { padding: "10px 5px", height: "30px" },
        }}
      />
      <Button size="small" onClick={handlePasteClick}>
        {t("account_settings.PASTE_CODE")}
      </Button>
    </Stack>
  )
}

export default OTPCodeField
